import { isProduction } from "utils/environment";
import { z } from "zod";
import { validateEmail } from "./util";

export const UserSchema = z.object({
  id: z.string().cuid(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().refine(validateEmail),
  avatarUrl: z.string().url().nullable(),
  isStaff: z.boolean(),
});

export const AccountSchema = z.object({
  id: z.number(),
  name: z.string(),
  owner_id: z.number(),
  logo_url: z.string(),
  is_paid: z.boolean(),
  is_unlimited: z.boolean(),
  is_team_account: z.boolean(),
  is_company_account: z.boolean(),
});
export type Account = z.infer<typeof AccountSchema>;

export const StripePlansSchema = z
  .enum([
    "price_1M71oeANrorjxqsOaLNWuuoR",
    "price_1MTrYOANrorjxqsO52f7Z4aX",
    "price_1NLSL4ANrorjxqsOM5tdu2hl",
    "price_1NLSOwANrorjxqsOGKcQulr3",
    "price_1NQsjgANrorjxqsOfHxQlF5a",
    "price_1NQskxANrorjxqsOIt3Vv2fd",
    "price_1NgTUcANrorjxqsOALqgSbQs",
    "price_1NgThTANrorjxqsOIlwwhTP0",
  ])
  .nullable();

export type StripePlan = z.infer<typeof StripePlansSchema>;

export const UserProfileSchema = z.object({
  id: z.string().cuid(),
  name: z.string(),
  zapierId: z.number(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  currentAccount: z.number(),
  avatarUrl: z.string().url().nullable(),
  planId: StripePlansSchema,
  accounts: z.array(AccountSchema).optional(),
  isStaff: z.boolean(),
  subscriptionPending: z.boolean().nullable(),
  createdAt: z.coerce.date(),
});

export type UserProfile = z.infer<typeof UserProfileSchema>;

export const UserAuthenticationSchema = z.object({
  id: z.number().transform((v) => v.toString()),
  url: z.string().url(),
  title: z.string().nullable(),
  label: z.string(),
});

export type UserAuthentication = z.infer<typeof UserAuthenticationSchema>;

export const AppSlugs = {
  INTERFACES: "interfaces",
  TABLES: "zapier-tables",
  OPENAI: isProduction ? "openai" : "openai-gpt-dalle-ca120564",
};

export const ZapierSessionSchema = z.union([
  z
    .object({
      id: z.number().int(),
      first_name: z.string(),
      last_name: z.string(),
      email: z.string(),
      email_confirmed: z.boolean(),
      current_account_id: z.number().int().nullable(),
      photo_url: z.string().nullable(),
      is_logged_in: z.literal(true),
      is_staff: z.boolean(),
      name: z.string().optional(),
    })
    .passthrough(),
  z
    .object({
      is_logged_in: z.literal(false),
    })
    .passthrough(),
]);

export type ZapierSession = z.infer<typeof ZapierSessionSchema>;

export const ZapierAccountsSchema = z
  .object({
    primary_id: z.number().int(),
    objects: z.array(AccountSchema).optional(),
  })
  .passthrough();

export type ZapierAccounts = z.infer<typeof ZapierAccountsSchema>;
